// Ingreso con PIN: habla con /api/pin-session y monta la sesión en el almacén
// aislado (ver pinStorage). Nunca guarda ni registra el PIN.
import { supabase } from './supabase';
import { activarModoPin, desactivarModoPin, guardarMeta, borrarMeta, modoPin } from './pinStorage';

const ENDPOINT = '/api/pin-session';
const DIA_MS = 24 * 3600 * 1000;
const CR_OFFSET_MS = 6 * 3600 * 1000; // America/Costa_Rica = UTC-6, sin horario de verano

// Timestamp (ms epoch) de la próxima medianoche de Costa Rica. A las 00:00:00
// exactas ya cuenta como el día nuevo: devuelve la medianoche siguiente.
export const proximaMedianocheCR = (ahora = new Date()) => {
  const localCR = ahora.getTime() - CR_OFFSET_MS;
  const inicioDiaCR = Math.floor(localCR / DIA_MS) * DIA_MS;
  return inicioDiaCR + DIA_MS + CR_OFFSET_MS;
};

const leerJson = async (r) => {
  try {
    return await r.json();
  } catch (e) {
    return null;
  }
};

export const entrarConPin = async (pin) => {
  let r;
  try {
    r = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pin }),
    });
  } catch (e) {
    return { ok: false, error: 'sin_conexion' };
  }

  const body = await leerJson(r);

  if (r.status === 200) {
    if (!body?.access_token || !body?.refresh_token) return { ok: false, error: 'servicio' };
    // El modo PIN se activa ANTES de setSession: setSession persiste la sesión
    // por el storage del cliente, y si el marcador no estuviera ya puesto la
    // escribiría en la cookie SSO compartida.
    activarModoPin();
    // La meta se guarda ANTES de setSession: setSession dispara SIGNED_IN y el
    // AuthContext la lee en ese mismo tick; si se guardara después, el contexto
    // la vería vacía y tomaría la sesión por vencida.
    guardarMeta({
      nombre: body.nombre,
      colaboradorId: body.colaborador_id,
      sesionId: typeof body.sesionId === 'string' ? body.sesionId : null,
      expiraEl: proximaMedianocheCR(),
    });
    try {
      const { error } = await supabase.auth.setSession({
        access_token: body.access_token,
        refresh_token: body.refresh_token,
      });
      if (error) throw error;
    } catch (e) {
      desactivarModoPin();
      return { ok: false, error: 'servicio' };
    }
    return { ok: true, nombre: body.nombre };
  }
  if (r.status === 401) {
    return {
      ok: false,
      error: 'invalido',
      intentosRestantes: body?.intentos_restantes,
      esperaSeg: body?.espere_seg,
    };
  }
  if (r.status === 429) return { ok: false, error: 'bloqueado', esperaSeg: body?.espere_seg };
  if (r.status === 400) return { ok: false, error: 'formato' };
  // La Function no tiene el secreto de la RPC: no se arregla reintentando.
  if (r.status === 500 && body?.error === 'configuracion') return { ok: false, error: 'configuracion' };
  return { ok: false, error: 'servicio' };
};

// signOut local mientras el modo PIN sigue activo: así supabase-js borra su
// clave del almacén PIN y no toca la cookie SSO. Después se limpia todo.
// De un solo vuelo: las llamadas concurrentes (init, suscripción, intervalo)
// esperan la misma promesa. Y sin marcador NO hay signOut: esta ruta nunca debe
// cerrar una sesión de cookie/SSO.
let enVuelo = null;
export const salirDePin = () => {
  if (enVuelo) return enVuelo;
  const trabajo = (async () => {
    if (modoPin()) {
      try {
        await supabase.auth.signOut({ scope: 'local' });
      } catch (e) { /* se limpia igual */ }
    }
    desactivarModoPin();
    borrarMeta();
  })();
  enVuelo = trabajo.finally(() => { enVuelo = null; });
  return enVuelo;
};

export const pinDisponible = async () => {
  try {
    const r = await fetch(ENDPOINT, { method: 'GET' });
    if (!r.ok) return false;
    return (await leerJson(r))?.pin === true;
  } catch (e) {
    return false;
  }
};
